import { faPalette, faFileInvoice, faFileInvoiceDollar, faArrowRight, faTasks, faFile, faInfoCircle, faStream, faLifeRing } from '@fortawesome/free-solid-svg-icons';
import { TasksTable, DesignsTable, QuotesTable, InvoicesTable, UploadsTable, FeedTable, ActionsDropdown, ActionsDropdownItem, ActionsDropdownDivider } from '../../tables';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAppContext } from '../../../context/AppContext';
import { fetchData, updateData, rememberAndTrackDesignsCreated } from '../../../services';
import { appendUploadsFromResults, getVisibleUploads } from '../../../utils/uploads';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { EditProjectForm, ExpediteProjectForm, HoldProjectForm, EngineeringHoldProjectForm, ShippingHoldProjectForm, EditAddressForm, EditFreightForm, ChangeStageForm, SupportForm, TicketForm, ManageProjectTagsForm, MarkStampedCompleteForm, MarkOpPackageCompleteForm, TransferProjectForm, UnarchiveProjectForm, NewNoteForm, NewFileForm, OrderShippedAlertForm, DrawingReviewAlertForm } from '../../forms';
import { Tracker } from '../../Tracker';
import { ProjectNameWithStatus } from '../../helpers/ProjectNameWithStatus';
import ConfirmDialog from '../../helpers/ConfirmDialog';
import { isEmptyAddress, isProjectArchived, projectAddressFromCompany } from '../../../utils/projects';
import { useAddressSourceCompany } from '../../../utils/useAddressSourceCompany';
import { countryDisplayName } from '../../../context/usStates';
const LIMITED_PROJECT_TAB_EMAILS = new Set([
    'packaging@mtsolar.us',
    'gillin@mtsolar.us'
]);

export const ProjectPage = ({ project }) => {
    console.log(`ProjectPage - project: ${project.id}`);
    console.log('ProjectPage - project.op_package_ordered:', project.op_package_ordered);
    console.log('ProjectPage - project.pe_stamps_ordered:', project.pe_stamps_ordered);
    const defaultDocumentTitle = 'MT Solar Project Manager';
    
    const [searchParams, setSearchParams] = useSearchParams();
    const [designs, setDesigns] = useState([]);
    const [archivedDesigns, setArchivedDesigns] = useState([]);
    const [quotes, setQuotes] = useState([]);
    const [files, setFiles] = useState([]);
    const [feedItems, setFeedItems] = useState([]);
    const [feedLoading, setFeedLoading] = useState(true);
    const [feedError, setFeedError] = useState('');
    /** Controlled file modal: traveler PDFs or design-associated photos (quotes/invoices subrows). */
    const [fileUploadForDesign, setFileUploadForDesign] = useState(null);
    const [newQuotePrefill, setNewQuotePrefill] = useState(null);
    const [designsFetched, setDesignsFetched] = useState(false);
    const [quotesFetched, setQuotesFetched] = useState(false);

    const getDefaultTab = useCallback((currentStage, designCount, quoteCount, designsKnown = true, quotesKnown = true, invoiceCount = 0) => {
        const isDataKnown = designsKnown && quotesKnown;

        if (invoiceCount > 0) {
            return 'invoices';
        }

        if (isDataKnown && designCount === 0) {
            return 'details';
        }

        if (isDataKnown && quoteCount > 0) {
            return 'quotes';
        }

        switch (currentStage) {
            case 'draft':
                return designCount > 0 ? 'designs' : 'details';
            case 'quoted':
            case 'prepare to build':
                return 'quotes';
            default:
                return 'designs';
        }
    }, []);
    
    const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
    const [isTicketFormOpen, setIsTicketFormOpen] = useState(false);
    const { token, base_url, setProjects, user, company, invoices } = useAppContext();
    const projectId = project.id;
    const projectCurrentStage = project.current_stage;
    const isSuperAdmin = user?.is_super_admin === true;
    const isLimitedProjectTabsUser = Boolean(
        user?.email && LIMITED_PROJECT_TAB_EMAILS.has(user.email.toLowerCase())
    );

    const validTabIds = useMemo(() => {
        if (isLimitedProjectTabsUser) {
            return ['quotes', 'invoices', 'files', 'tasks'];
        }
        if (isSuperAdmin) {
            return ['details', 'designs', 'quotes', 'invoices', 'files', 'feed', 'tasks'];
        }
        return ['details', 'designs', 'quotes', 'invoices', 'files'];
    }, [isLimitedProjectTabsUser, isSuperAdmin]);

    const handleUploadsCreated = useCallback((results) => {
        setFiles((prev) => appendUploadsFromResults(prev, results));
    }, []);

    const controlledUploadPreset = useMemo(() => {
        if (!fileUploadForDesign?.design?.id) return null;
        const { mode, design } = fileUploadForDesign;
        const uniqueKey =
            design.unique_id != null && design.unique_id !== ''
                ? design.unique_id
                : String(design.id);
        return {
            association_type: 'design',
            associated_id: design.id,
            is_traveler: mode === 'traveler',
            is_packaging_photo: mode === 'design_photos',
            is_packaging_photos: mode === 'design_photos',
            restrict_to_images: mode === 'design_photos',
            rename_uploads_with_design_suffix: mode === 'design_photos',
            design_unique_id: uniqueKey,
        };
    }, [fileUploadForDesign]);

    const handleUploadTravelerForDesign = useCallback((design) => {
        if (!design?.id) return;
        setFileUploadForDesign({ mode: 'traveler', design });
    }, []);

    const handleUploadPhotosForDesign = useCallback((design) => {
        if (!design?.id) return;
        setFileUploadForDesign({ mode: 'design_photos', design });
    }, []);

    const navigate = useNavigate();

    const passedDesigns = useMemo(
        () => designs.filter((d) => d.design_passed === true),
        [designs]
    );

    const projectInvoices = useMemo(
        () => invoices.filter((invoice) => invoice.project_site_id === project.id),
        [invoices, project.id]
    );

    // Get active tab from URL or use default based on project stage
    const tabFromUrl = searchParams.get('tab');
    const defaultTabForUser = getDefaultTab(
        project.current_stage,
        designs.length,
        quotes.length,
        designsFetched,
        quotesFetched,
        projectInvoices.length
    );
    const rawActiveTab = validTabIds.includes(tabFromUrl)
        ? tabFromUrl
        : defaultTabForUser;
    const activeTab = validTabIds.includes(rawActiveTab) ? rawActiveTab : 'quotes';
    
    const setActiveTab = useCallback((tab) => {
        setSearchParams({ tab }, { replace: true });
    }, [setSearchParams]);

    const handleGetQuoteForDesign = useCallback((design) => {
        if (!design?.id) return;
        setNewQuotePrefill({ designId: design.id, requestId: Date.now() });
        setActiveTab('quotes');
    }, [setActiveTab]);

    const clearNewQuotePrefill = useCallback(() => {
        setNewQuotePrefill(null);
    }, []);

    // Build tabs array based on user permissions and data
    let allTabs;
    if (isLimitedProjectTabsUser) {
        allTabs = [
            { id: 'quotes', label: 'Quotes', icon: faFileInvoice },
            { id: 'invoices', label: 'Invoices', icon: faFileInvoiceDollar },
            { id: 'files', label: 'Files', icon: faFile },
            { id: 'tasks', label: 'Tasks', icon: faTasks },
        ];
    } else {
        allTabs = [
            { id: 'details', label: 'Details', icon: faInfoCircle },
            { id: 'designs', label: 'Designs', icon: faPalette },
            { id: 'quotes', label: 'Quotes', icon: faFileInvoice },
            { id: 'invoices', label: 'Invoices', icon: faFileInvoiceDollar },
            { id: 'files', label: 'Files', icon: faFile },
            ...(isSuperAdmin ? [{ id: 'feed', label: 'Feed', icon: faStream }] : []),
            // { id: 'comments', label: 'Comments', icon: faComments }
        ];
        if (isSuperAdmin) {
            allTabs.push({ id: 'tasks', label: 'All Tasks', icon: faTasks });
        }
    }

    const tabs = allTabs;

    const getTabCount = (tabId) => {
        switch (tabId) {
            case 'designs':
                return passedDesigns.length;
            case 'quotes':
                return quotes.length;
            case 'invoices':
                return projectInvoices.length;
            case 'files':
                return getVisibleUploads(files, user).length;
            case 'feed':
                return feedItems.length;
            case 'tasks':
                return project.project_tasks ? project.project_tasks.length : 0;
            default:
                return 0;
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
    };

    const handleNoteCreated = useCallback((response, noteBody) => {
        const now = new Date().toISOString();
        const responseItem = response?.feed_item || response?.item || response?.note || response?.feed || null;
        const normalizedItem = Array.isArray(responseItem) ? responseItem[0] : responseItem;

        const newFeedItem = {
            id: normalizedItem?.id || `local-note-${Date.now()}`,
            object_id: normalizedItem?.object_id || normalizedItem?.id || '',
            object_type: normalizedItem?.object_type || 'note',
            source: normalizedItem?.source || 'portal',
            title: normalizedItem?.title || noteBody,
            summary: normalizedItem?.summary || noteBody,
            timestamp: normalizedItem?.timestamp || normalizedItem?.created_at || now,
            created_at: normalizedItem?.created_at || now,
            updated_at: normalizedItem?.updated_at || now,
            details: normalizedItem?.details || {}
        };

        setFeedItems(prev => {
            const withoutDuplicate = prev.filter(item => item.id !== newFeedItem.id);
            return [newFeedItem, ...withoutDuplicate];
        });
    }, []);

    useEffect(() => {
        setDesignsFetched(false);
        setQuotesFetched(false);
        let cancelled = false;

        const loadProjectDesigns = async () => {
            try {
                const designs = await fetchData(token, `${base_url}/apis/designs/${projectId}`);
                if (cancelled) return;

                if (designs.designs) {
                    // Separate active and archived designs
                    const activeDesigns = designs.designs.filter(d => !d.is_archived);
                    const archivedDesigns = designs.designs.filter(d => d.is_archived);
                    rememberAndTrackDesignsCreated(designs.designs, {
                        companyId: project.company_id || company?.id || user?.company_id,
                        projectId,
                    });
                    setDesigns(activeDesigns);
                    setArchivedDesigns(archivedDesigns);
                }
            } catch (error) {
                console.error('Error loading user and company:', error);
            } finally {
                if (!cancelled) setDesignsFetched(true);
            }
        }

        const loadProjectQuotes = async () => {
            try {
            const quotes = await fetchData(token, `${base_url}/apis/billing?project_id=${projectId}`);
                if (cancelled) return;
                if (quotes.quotes) {
                    setQuotes(quotes.quotes);
                }
                if (quotes.quotes?.length > 0 && projectCurrentStage === 'draft') {
                   const updatedProject = await updateData(token, `${base_url}/apis/projects/${projectId}`, { current_stage: 'quoted' });
                   if (updatedProject.project) {
                    setProjects(prevProjects => prevProjects.map(p => p.id === projectId ? updatedProject.project : p));
                   }
                }
                if (quotes.quotes) {
                    quotes.quotes.forEach(async (quote) => {
                        if (quote.is_accepted && projectCurrentStage === 'quoted') {
                            const updatedProject = await updateData(token, `${base_url}/apis/projects/${projectId}`, { current_stage: 'prepare to build' });
                            if (updatedProject.project) {
                                setProjects(prevProjects => prevProjects.map(p => p.id === projectId ? updatedProject.project : p));
                            }
                        }
                    });
                }
            } catch (error) {
                console.error('Error loading project quotes:', error);
            } finally {
                if (!cancelled) setQuotesFetched(true);
            }
        }

        const loadProjectUploads = async () => {
            try {
                const response = await fetchData(token, `${base_url}/apis/uploads/get-uploads/${projectId}`);
                if (cancelled) return;
                if (response.uploads) {
                    setFiles(response.uploads);
                }
            } catch (error) {
                console.error('Error loading project uploads:', error);
            }
        }

        const REFRESH_MS = 60_000;

        loadProjectDesigns();
        loadProjectQuotes();
        loadProjectUploads();

        const designsInterval = setInterval(loadProjectDesigns, REFRESH_MS);
        const quotesInterval = setInterval(loadProjectQuotes, REFRESH_MS);
        const uploadsInterval = setInterval(loadProjectUploads, REFRESH_MS);

        return () => {
            cancelled = true;
            clearInterval(designsInterval);
            clearInterval(quotesInterval);
            clearInterval(uploadsInterval);
        };
    }, [projectId, projectCurrentStage, base_url, token, setProjects, project.company_id, company?.id, user?.company_id]);

    useEffect(() => {
        const loadProjectFeed = async () => {
            if (!isSuperAdmin) {
                setFeedItems([]);
                setFeedError('');
                setFeedLoading(false);
                return;
            }
            setFeedLoading(true);
            setFeedError('');
            try {
                const response = await fetchData(token, `${base_url}/apis/projects/get-feed/${project.id}`);
                setFeedItems(response?.feed || []);
            } catch (error) {
                console.error('Error loading project feed:', error);
                setFeedError('Unable to load project feed.');
            } finally {
                setFeedLoading(false);
            }
        };

        loadProjectFeed();
    }, [project.id, base_url, token, isSuperAdmin]);

    useEffect(() => {
        // Wait for initial data fetches so we persist a URL tab based on loaded project data.
        if (!designsFetched || !quotesFetched) return;
        if (!searchParams.get('tab')) {
            const next = getDefaultTab(
                project.current_stage,
                designs.length,
                quotes.length,
                true,
                true,
                projectInvoices.length
            );
            setActiveTab(next);
        }
    }, [project.current_stage, searchParams, setActiveTab, getDefaultTab, designs.length, quotes.length, designsFetched, quotesFetched, projectInvoices.length]);

    useEffect(() => {
        if (!isLimitedProjectTabsUser) return;
        const t = searchParams.get('tab');
        if (t && !validTabIds.includes(t)) {
            setSearchParams({ tab: 'quotes' }, { replace: true });
        }
    }, [isLimitedProjectTabsUser, searchParams, setSearchParams, validTabIds]);

    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab === 'billing') {
            setSearchParams({ tab: 'quotes' }, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    useEffect(() => {
        const tab = searchParams.get('tab');
        if (tab === 'failed-designs' || tab === 'archived-designs') {
            setSearchParams({ tab: 'designs' }, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // Billing same as shipping: compare address objects
    const addressesMatch = useMemo(() => {
        const s = project.shipping_address;
        const b = project.billing_address;
        if (!s || !b) return false;
        return (
            (s.full_name || '') === (b.full_name || '') &&
            (s.email || '') === (b.email || '') &&
            (s.street1 || '') === (b.street1 || '') &&
            (s.street2 || '') === (b.street2 || '') &&
            (s.city || '') === (b.city || '') &&
            (s.state || '') === (b.state || '') &&
            (s.zip || '') === (b.zip || '') &&
            (s.country || '') === (b.country || '')
        );
    }, [project.shipping_address, project.billing_address]);

    const [billingSameAsShipping, setBillingSameAsShipping] = useState(() => addressesMatch);
    useEffect(() => {
        setBillingSameAsShipping(addressesMatch);
    }, [project.id, addressesMatch]);

    const [billingAddressAction, setBillingAddressAction] = useState(null);
    const [companyAddressAction, setCompanyAddressAction] = useState(null);
    const [showMissingCompanyAddressPrompt, setShowMissingCompanyAddressPrompt] = useState(false);
    const { addressCompany, isLoadingOwningCompany } = useAddressSourceCompany(project);
    const canCopyCompanyAddress = isSuperAdmin
        ? Boolean(project.company_id)
        : Boolean(addressCompany);
    const companyAdminPath = isSuperAdmin
        ? (project.company_id ? `/super-admin?companyId=${project.company_id}` : '/super-admin')
        : '/admin';
    const missingCompanyAddressMessage = isSuperAdmin
        ? `There's no address on ${project.company_name ? `${project.company_name}'s` : "this company's"} profile to copy. Open the company in Super Admin and update its details, then try again.`
        : "There's no address on your company profile to copy. Go to the Admin page and update your company details, then try again.";

    const handleCopyCompanyAddress = async (addressType) => {
        const mapped = projectAddressFromCompany(addressCompany, addressType);
        if (!mapped) {
            setShowMissingCompanyAddressPrompt(true);
            return;
        }
        setCompanyAddressAction(addressType);
        try {
            const existing = addressType === 'shipping' ? project.shipping_address : project.billing_address;
            const data = {
                [addressType === 'shipping' ? 'shipping_address' : 'billing_address']: {
                    ...(existing && typeof existing === 'object' ? existing : {}),
                    full_name: mapped.full_name || '',
                    email: mapped.email || '',
                    phone: mapped.phone || '',
                    street1: mapped.street1 || '',
                    street2: mapped.street2 || '',
                    city: mapped.city || '',
                    state: mapped.state || '',
                    zip: mapped.zip || '',
                    country: mapped.country || '',
                }
            };
            const updated = await updateData(token, `${base_url}/apis/projects/${project.id}`, data);
            if (updated.project) {
                setProjects((prev) => prev.map((p) => (p.id === project.id ? updated.project : p)));
                if (typeof window.showAlert === 'function') {
                    const label = addressType === 'billing' ? 'Billing' : 'Shipping';
                    const sourceName = addressCompany?.company_name;
                    window.showAlert(
                        200,
                        sourceName ? `${label} address copied from ${sourceName}.` : `${label} address copied from company.`
                    );
                }
            }
        } catch (err) {
            console.error('Error copying company address:', err);
            if (typeof window.showAlert === 'function') {
                window.showAlert(500, 'Failed to copy company address. Please try again.');
            }
        } finally {
            setCompanyAddressAction(null);
        }
    };

    const handleUseShippingForBilling = async () => {
        const shipping = project.shipping_address;
        if (isEmptyAddress(shipping)) return;
        setBillingAddressAction('copy');
        try {
            const data = {
                billing_address: {
                    full_name: shipping.full_name || '',
                    email: shipping.email || '',
                    street1: shipping.street1 || '',
                    street2: shipping.street2 || '',
                    city: shipping.city || '',
                    state: shipping.state || '',
                    zip: shipping.zip || '',
                    country: shipping.country || ''
                }
            };
            const updated = await updateData(token, `${base_url}/apis/projects/${project.id}`, data);
            if (updated.project) {
                setProjects(prev => prev.map(p => p.id === project.id ? updated.project : p));
                setBillingSameAsShipping(true);
                if (typeof window.showAlert === 'function') {
                    window.showAlert(200, 'Billing address set to shipping address.');
                }
            }
        } catch (err) {
            console.error('Error copying shipping to billing:', err);
            if (typeof window.showAlert === 'function') {
                window.showAlert(500, 'Failed to update billing address. Please try again.');
            }
        } finally {
            setBillingAddressAction(null);
        }
    };

    const handleClearBillingAddress = async () => {
        setBillingAddressAction('clear');
        try {
            const data = {
                billing_address: {
                    full_name: '',
                    email: '',
                    street1: '',
                    street2: '',
                    city: '',
                    state: '',
                    zip: '',
                    country: ''
                }
            };
            const updated = await updateData(token, `${base_url}/apis/projects/${project.id}`, data);
            if (updated.project) {
                setProjects(prev => prev.map(p => p.id === project.id ? updated.project : p));
                setBillingSameAsShipping(false);
                if (typeof window.showAlert === 'function') {
                    window.showAlert(200, 'Billing address cleared.');
                }
            }
        } catch (err) {
            console.error('Error clearing billing address:', err);
            if (typeof window.showAlert === 'function') {
                window.showAlert(500, 'Failed to clear billing address. Please try again.');
            }
        } finally {
            setBillingAddressAction(null);
        }
    };

    const handleBack = () => {
        navigate('/');
    };

    useEffect(() => {
        const projectName = project?.project_name?.trim();
        document.title = projectName
            ? `${projectName} | ${defaultDocumentTitle}`
            : defaultDocumentTitle;

        return () => {
            document.title = defaultDocumentTitle;
        };
    }, [project?.project_name]);

    const renderRoadmap = () => (
        <div className="project-roadmap">
            <h3 className="roadmap-title">Roadmap for Your Project</h3>
            <div className="roadmap-steps">
                <div 
                    className={`roadmap-step ${passedDesigns.length > 0 ? 'completed' : 'active'} clickable`}
                    onClick={() => setActiveTab('designs')}
                >
                    <div className="roadmap-step-number">1</div>
                    <div className="step-content">
                        <h4>Create a Design for Your Solar Array</h4>
                    </div>
                </div>
                <div 
                    className={`roadmap-step ${quotes.length > 0 ? 'completed' : passedDesigns.length > 0 ? 'active' : ''} ${passedDesigns.length > 0 ? 'clickable' : ''}`}
                    onClick={() => passedDesigns.length > 0 && setActiveTab('quotes')}
                >
                    <div className="roadmap-step-number">2</div>
                    <div className="step-content">
                        <h4>Get a Quote for Your Project</h4>
                    </div>
                </div>
                <div 
                    className={`roadmap-step ${quotes.some(q => q.is_accepted) ? 'completed' : quotes.length > 0 ? 'active' : ''} ${quotes.length > 0 ? 'clickable' : ''}`}
                    onClick={() => quotes.length > 0 && setActiveTab('quotes')}
                >
                    <div className="roadmap-step-number">3</div>
                    <div className="step-content">
                        <h4>Review and Accept Your Quote</h4>
                    </div>
                </div>
                {(() => {
                    const allInvoicesPaid = projectInvoices.length > 0 && projectInvoices.every(invoice => invoice.invoice_status?.toLowerCase() === 'paid');
                    const isActiveStage = !['draft', 'quoted'].includes(project.current_stage);
                    return (
                        <div 
                            className={`roadmap-step ${allInvoicesPaid ? 'completed' : isActiveStage ? 'active' : ''} ${quotes.some(q => q.is_accepted) ? 'clickable' : ''}`}
                            onClick={() => quotes.some(q => q.is_accepted) && setActiveTab('invoices')}
                        >
                            <div className="roadmap-step-number">4</div>
                            <div className="step-content">
                                <h4>Pay Your Invoice</h4>
                            </div>
                        </div>
                    );
                })()}
            </div>
        </div>
    );

    const renderTabContent = () => {
        switch (activeTab) {
            case 'details':
                return (
                    <div className="tab-content">
                        <div className="project-info">
                            <EditProjectForm project={project} />
                            <div className="project-info-row">
                                {project.estimated_completion_date && (
                                    <p data-label="Estimated Completion Date">{formatDate(project.estimated_completion_date)}</p>
                                )}
                                <p data-label="Project Type">{project.project_type}</p>
                                {isSuperAdmin && (
                                    <p data-label="Lead Time at Order">{project.lead_time_at_order || '—'}</p>
                                )}
                            </div>
                            {project.description && (<p data-label="Description" className="description-row">{project.description}</p>)}
                            {isSuperAdmin && (
                                <p data-label="Shipping Details" className="description-row">
                                    {project.shipping_address?.details || '—'}
                                </p>
                            )}
                            <div className="project-info-row project-info-row--with-action">
                                <p data-label="Freight Carrier">{project.freight_carrier || '—'}</p>
                                <p data-label="Tracking Number">{project.tracking_number || '—'}</p>
                                {isSuperAdmin && <EditFreightForm project={project} />}
                            </div>
                            <div className="addresses-container">
                                <div className="address-column">
                                    <div className="address-label-spacer" aria-hidden="true" />
                                    <div data-label="Site Address">
                                        {project.site_address_1 && <p>{project.site_address_1}</p>}
                                        {project.site_address_2 && <p>{project.site_address_2}</p>}
                                        {(project.city || project.state || project.zip_code || project.country) && (
                                            <p>
                                                {[project.city, project.state, project.zip_code].filter(Boolean).join(', ')}
                                            </p>
                                        )}
                                        {(project.country || project.state) && (
                                            <p>{countryDisplayName(project.country, project.state)}</p>
                                        )}
                                    </div>
                                </div>
                                <div className="address-column">
                                    {canCopyCompanyAddress ? (
                                        <div className="billing-same-as-shipping">
                                            <button
                                                type="button"
                                                className="copy-company-address-button"
                                                onClick={() => handleCopyCompanyAddress('shipping')}
                                                disabled={Boolean(companyAddressAction) || isLoadingOwningCompany}
                                                title={addressCompany?.company_name ? `Copy address from ${addressCompany.company_name}` : undefined}
                                            >
                                                Use company address
                                            </button>
                                            {companyAddressAction === 'shipping' && (
                                                <span className="billing-copy-status">Copying…</span>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="address-label-spacer" aria-hidden="true" />
                                    )}
                                    <div data-label="Shipping Address">
                                        {!isEmptyAddress(project.shipping_address) ? (
                                            <>
                                                <p>{project.shipping_address.full_name}</p>
                                                <p>{project.shipping_address.email}</p>
                                                {project.shipping_address.phone && <p>{project.shipping_address.phone}</p>}
                                                <p>{project.shipping_address.street1}</p>
                                                {project.shipping_address.street2 && <p>{project.shipping_address.street2}</p>}
                                                <p>{[project.shipping_address.city, project.shipping_address.state, project.shipping_address.zip].filter(Boolean).join(', ')}</p>
                                                <p>{countryDisplayName(project.shipping_address.country, project.shipping_address.state)}</p>
                                            </>
                                        ) : (
                                            <p>Shipping Address not provided yet.</p>
                                        )}
                                        <EditAddressForm project={project} addressType="shipping" />
                                    </div>
                                </div>
                                <div className="address-column">
                                    <div className="address-column-actions">
                                        {canCopyCompanyAddress && (
                                            <div className="billing-same-as-shipping">
                                                <button
                                                    type="button"
                                                    className="copy-company-address-button"
                                                    onClick={() => handleCopyCompanyAddress('billing')}
                                                    disabled={Boolean(companyAddressAction) || isLoadingOwningCompany}
                                                    title={addressCompany?.company_name ? `Copy address from ${addressCompany.company_name}` : undefined}
                                                >
                                                    Use company address
                                                </button>
                                                {companyAddressAction === 'billing' && (
                                                    <span className="billing-copy-status">Copying…</span>
                                                )}
                                            </div>
                                        )}
                                        {!isEmptyAddress(project.shipping_address) ? (
                                            <div className="billing-same-as-shipping">
                                                <label className="checkbox-label" title={billingSameAsShipping ? 'Uncheck to use a different billing address' : ''}>
                                                    <input
                                                        type="checkbox"
                                                        checked={billingSameAsShipping}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                handleUseShippingForBilling();
                                                            } else {
                                                                handleClearBillingAddress();
                                                            }
                                                        }}
                                                        disabled={isEmptyAddress(project.shipping_address) || billingAddressAction}
                                                    />
                                                    <span>Billing address same as shipping address</span>
                                                    {billingAddressAction === 'copy' && <span className="billing-copy-status">Applying…</span>}
                                                    {billingAddressAction === 'clear' && <span className="billing-copy-status">Clearing…</span>}
                                                </label>
                                            </div>
                                        ) : (
                                            !canCopyCompanyAddress && <div className="address-label-spacer" aria-hidden="true" />
                                        )}
                                    </div>
                                    <div data-label="Billing Address">
                                        {project.billing_address ? (
                                            <>
                                                <p>{project.billing_address.full_name}</p>
                                                <p>{project.billing_address.email}</p>
                                                {project.billing_address.phone && <p>{project.billing_address.phone}</p>}
                                                <p>{project.billing_address.street1}</p>
                                                {project.billing_address.street2 && <p>{project.billing_address.street2}</p>}
                                                <p>{[project.billing_address.city, project.billing_address.state, project.billing_address.zip].filter(Boolean).join(', ')}</p>
                                                <p>{countryDisplayName(project.billing_address.country, project.billing_address.state)}</p>
                                            </>
                                        ) : (
                                            <p>Billing Address not provided yet.</p>
                                        )}
                                        <EditAddressForm project={project} addressType="billing" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 'designs':
                return (
                    <div className="tab-content">
                        <DesignsTable
                            project={project}
                            designs={designs}
                            setDesigns={setDesigns}
                            archivedDesigns={archivedDesigns}
                            setArchivedDesigns={setArchivedDesigns}
                            files={files}
                            showFailedToggle={isSuperAdmin}
                            onGetQuoteForDesign={handleGetQuoteForDesign}
                        />
                    </div>
                );
            case 'files':
                return (
                    <div className="tab-content">
                        <UploadsTable
                            project={project}
                            files={files}
                            setFiles={setFiles}
                            designs={designs}
                        />
                    </div>
                );
            case 'feed':
                return (
                    <div className="tab-content">
                        <div className="tab-content-toolbar">
                            <NewNoteForm project={project} onNoteCreated={handleNoteCreated} />
                        </div>
                        <FeedTable feed={feedItems} loading={feedLoading} error={feedError} />
                    </div>
                );
            case 'comments':
                return <div className="tab-content">Comments content goes here</div>;
            case 'tasks':
                return <div className="tab-content"><TasksTable project={project} user={user} /></div>;
            case 'quotes': {
                const quotesTableProps = {
                    project,
                    designs,
                    files,
                    quotes,
                    setQuotes,
                    onUploadTraveler: handleUploadTravelerForDesign,
                    onUploadPhotosForDesign: handleUploadPhotosForDesign,
                    newQuotePrefill,
                    onNewQuotePrefillConsumed: clearNewQuotePrefill,
                };
                return (
                    <div className="tab-content">
                        <QuotesTable {...quotesTableProps} />
                    </div>
                );
            }
            case 'invoices':
                return (
                    <div className="tab-content">
                        <InvoicesTable
                            designs={designs}
                            files={files}
                            invoices={projectInvoices}
                            hideProjectName
                            onUploadTraveler={handleUploadTravelerForDesign}
                            onUploadPhotosForDesign={handleUploadPhotosForDesign}
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    if (isProjectArchived(project) && !isSuperAdmin) {
        return (
            <div className="page">
                <div className="main-content">
                    <button className="back-button" onClick={handleBack}>
                        ← Back to Dashboard
                    </button>
                    <div className="admin-empty-state">
                        <h2>This project is archived</h2>
                        <p>
                            This project is no longer accessible. Contact support if you need to view it.
                        </p>
                        <SupportForm defaultProject={project.project_name} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page">
            <div className="main-content">
                <div className="project-header">
                    <div className="project-header-row">
                        {/* Left section - Project info + stage tracker */}
                        <div className="project-header-left">
                            <button className="back-button" onClick={handleBack}>
                                ← Back to Dashboard
                            </button>
                            <div className="project-header-title-row">
                                {project.project_name && (
                                    <ProjectNameWithStatus project={project} variant="heading" />
                                )}
                                <div className="project-header-tracker">
                                    <Tracker currentStage={project.current_stage} />
                                </div>
                            </div>
                            <small className="project-meta">
                                Created: {formatDate(project.created_at)}
                            </small>
                            <small className="project-meta">
                                Created By:{' '}
                                {isSuperAdmin && project.company_id ? (
                                    <button
                                        type="button"
                                        className="project-meta-link"
                                        onClick={() => navigate(`/super-admin?companyId=${project.company_id}`)}
                                    >
                                        {project.company_name}
                                    </button>
                                ) : (
                                    project.company_name
                                )}
                            </small>
                        </div>

                        {/* Right section - Actions */}
                        <div className="project-header-right">
                            {user && user.is_super_admin && (
                                <div className="header-actions">
                                    <div className="header-actions-buttons">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => setIsTicketFormOpen(true)}
                                        >
                                            <FontAwesomeIcon icon={faLifeRing} /> Submit support ticket
                                        </button>
                                        <ActionsDropdown
                                            isOpen={adminDropdownOpen}
                                            onOpenChange={setAdminDropdownOpen}
                                            label="Admin Actions"
                                            title="Admin Actions"
                                            buttonClassName="btn btn-secondary actions-dropdown-btn"
                                        >
                                            {project.hubspot_id && (
                                                <ActionsDropdownItem>
                                                    <a href={`https://app.hubspot.com/contacts/8953044/record/0-3/${project.hubspot_id}`} target="_blank" rel="noreferrer" className="btn btn-action btn-primary">
                                                        View in HubSpot <FontAwesomeIcon icon={faArrowRight} />
                                                    </a>
                                                </ActionsDropdownItem>
                                            )}
                                            {project.hubspot_id && (
                                                <ActionsDropdownDivider />
                                            )}
                                            <ActionsDropdownItem>
                                                <TransferProjectForm project={project} />
                                            </ActionsDropdownItem>
                                            <ActionsDropdownItem>
                                                <ChangeStageForm project={project} />
                                            </ActionsDropdownItem>
                                            <ActionsDropdownDivider />
                                            <ActionsDropdownItem>
                                                <ExpediteProjectForm project={project} />
                                            </ActionsDropdownItem>
                                            <ActionsDropdownItem>
                                                <HoldProjectForm project={project} />
                                            </ActionsDropdownItem>
                                            <ActionsDropdownItem>
                                                <EngineeringHoldProjectForm project={project} />
                                            </ActionsDropdownItem>
                                            <ActionsDropdownItem>
                                                <ShippingHoldProjectForm project={project} />
                                            </ActionsDropdownItem>
                                            <ActionsDropdownDivider />
                                            <ActionsDropdownItem>
                                                <OrderShippedAlertForm project={project} />
                                            </ActionsDropdownItem>
                                            <ActionsDropdownItem>
                                                <DrawingReviewAlertForm project={project} />
                                            </ActionsDropdownItem>
                                            <ActionsDropdownDivider />
                                            <ActionsDropdownItem>
                                                <ManageProjectTagsForm project={project} />
                                            </ActionsDropdownItem>
                                            {isProjectArchived(project) && (
                                                <>
                                                    <ActionsDropdownDivider />
                                                    <ActionsDropdownItem>
                                                        <UnarchiveProjectForm project={project} />
                                                    </ActionsDropdownItem>
                                                </>
                                            )}
                                        </ActionsDropdown>
                                    </div>
                                    {(project.pe_stamps_ordered || project.op_package_ordered) && (
                                        <div className="header-actions-tags">
                                            {project.pe_stamps_ordered && (
                                                <MarkStampedCompleteForm project={project} user={user} />
                                            )}
                                            {project.op_package_ordered && (
                                                <MarkOpPackageCompleteForm project={project} user={user} />
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {user && user.is_super_admin === false && (
                                <div className="header-actions">
                                    <div className="header-actions-buttons">
                                        <SupportForm defaultProject={project.project_name} />
                                    </div>
                                    {(project.pe_stamps_ordered || project.op_package_ordered) && (
                                        <div className="header-actions-tags">
                                            {project.pe_stamps_ordered && (
                                                <MarkStampedCompleteForm project={project} user={user} />
                                            )}
                                            {project.op_package_ordered && (
                                                <MarkOpPackageCompleteForm project={project} user={user} />
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <hr />
                </div>

                <div className="project-container">
                    <NewFileForm
                        project={project}
                        showTriggerButtons={false}
                        controlledModalOpen={fileUploadForDesign !== null}
                        onControlledModalClose={() => setFileUploadForDesign(null)}
                        uploadPreset={controlledUploadPreset}
                        onUploadSuccess={handleUploadsCreated}
                    />
                    <div className="project-nav">
                        <div className="nav-tabs">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab.id)}
                                >
                                    <FontAwesomeIcon icon={tab.icon} className="tab-icon" />
                                    <span>{tab.label}</span>
                                    {tab.id !== 'details' && <span className="tab-count">{getTabCount(tab.id)}</span>}
                                </button>
                            ))}
                        </div>
                        <div className={`project-tab-layout ${activeTab === 'details' ? 'tab-details' : ''}`}>
                            <div className="project-tab-main">
                                {renderTabContent()}
                            </div>
                            {!isLimitedProjectTabsUser && (
                                <aside className={`project-roadmap-sidebar ${activeTab === 'details' ? 'roadmap-visible-mobile' : ''}`}>
                                    {renderRoadmap()}
                                </aside>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {isSuperAdmin && (
                <TicketForm
                    isOpen={isTicketFormOpen}
                    onClose={() => setIsTicketFormOpen(false)}
                    defaultTicketType="support"
                    defaultProjectId={project.id}
                />
            )}
            <ConfirmDialog
                open={showMissingCompanyAddressPrompt}
                onOpenChange={setShowMissingCompanyAddressPrompt}
                title="Company address not set"
                message={missingCompanyAddressMessage}
                confirmLabel={isSuperAdmin ? 'Open company' : 'Go to Admin'}
                cancelLabel="Close"
                onConfirm={() => {
                    navigate(companyAdminPath);
                }}
            />
        </div>
    )
}